import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

interface OrganizationBreadcrumbProps {
  organizationId: string;
  className?: string;
}

type Organization = Tables<'organizations'>;
type SimplifiedOrganization = Pick<Organization, 'id' | 'name'>;

export function OrganizationBreadcrumb({ organizationId, className }: OrganizationBreadcrumbProps) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [ancestorChain, setAncestorChain] = useState<SimplifiedOrganization[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);

        // Fetch current organization
        const { data: org, error } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', organizationId)
          .single();

        if (error) throw error;
        if (!org) return;

        setOrganization(org);

        // If no parent, skip ancestor chain
        if (!org.hierarchy_path || org.hierarchy_path.length <= 1) {
          setAncestorChain([]);
          return;
        }

        // Remove the last item (current org) from hierarchy
        const ancestorIds = org.hierarchy_path.slice(0, -1);

        if (ancestorIds.length > 0) {
          // Fetch all ancestors
          const { data: ancestors, error: ancestorError } = await supabase
            .from('organizations')
            .select('id, name')
            .in('id', ancestorIds);

          if (ancestorError) throw ancestorError;

          // Sort ancestors according to hierarchy path
          const sortedAncestors = ancestorIds
            .map((id) => ancestors?.find((a) => a.id === id))
            .filter((a) => a !== undefined) as SimplifiedOrganization[];

          setAncestorChain(sortedAncestors);
        }
      } catch (error) {
        // Error handling omitted intentionally
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchOrganization();
    }
  }, [organizationId]);

  if (loading || !organization) {
    return <div className={`${className || ''} animate-pulse h-6 w-48 bg-gray-200 rounded`} />;
  }

  return (
    <div className={`flex items-center text-sm text-muted-foreground ${className || ''}`}>
      <Link to="/organizations" className="hover:text-primary">
        Organizations
      </Link>

      {ancestorChain.map((ancestor, i) => (
        <React.Fragment key={ancestor.id}>
          <ChevronRight className="h-4 w-4 mx-1" />
          <Link to={`/organizations/${ancestor.id}`} className="hover:text-primary">
            {ancestor.name}
          </Link>
        </React.Fragment>
      ))}

      <ChevronRight className="h-4 w-4 mx-1" />
      <span className="font-medium text-foreground">{organization.name}</span>
    </div>
  );
}
